import React, { useState, useEffect } from 'react';
import logger from 'src/logger';
import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  Box,
  TextField,
  FormControl,
  FormHelperText,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Popover,
  InputAdornment,
  Autocomplete,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  RadioGroup,
  Radio,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import MapIcon from '@mui/icons-material/Map';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { commonCoarseFishSpecies, commonSeaFishSpecies } from 'src/fishSpecies';
import { WeightInput } from '../Components/WeightInput';
import { FileInfo } from '../Types';

const priceOptions = [1, 2, 5, 10, 20, 50, 100];

interface ImageCardProps {
  data: FileInfo;
  handlePriceChange: any;
  handleTagsChange: any;
  handleSpeciesChange: any;
  handleWeightChange: any;
  handleLocationChange: any;
  handleAreaChange: any;
  handlePublicizeLocationChange: any;
  handleChargeLocationChange: any;
  handleFreeForRegisteredUsersChange: any;
  handleFreeForEveryoneChange: any;
  handleMinimumPhotoUploadsChange: any;
  isSubmitted: boolean;
  stripeOnboardingComplete: boolean;
  handleOpenMap: (latitude: number, longitude: number) => void;
}

const ImageCard = ({
  data,
  handlePriceChange,
  handleTagsChange,
  handleSpeciesChange,
  handleWeightChange,
  handleLocationChange,
  handleAreaChange,
  handlePublicizeLocationChange,
  handleChargeLocationChange,
  handleFreeForRegisteredUsersChange,
  handleFreeForEveryoneChange,
  handleMinimumPhotoUploadsChange,
  isSubmitted,
  stripeOnboardingComplete,
  handleOpenMap,
}: ImageCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for controlling the location popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // State to control the Fees Info Dialog
  const [openFeesInfo, setOpenFeesInfo] = useState(false);

  // State to control the location info modal
  const [showLocationInfo, setShowLocationInfo] = useState(false);

  // State to control the fees table expansion
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  // Ensure logical consistency when dependencies change
  useEffect(() => {
    // Only run this effect when the component mounts or when data changes from external sources
    // Don't run it in response to our own handler calls to avoid infinite loops
  }, []); // Empty dependency array to only run once on mount

  const handlePublicizeChange = (e) => {
    const newValue = e.target.checked;
    logger.debug('handlePublicizeChange called with:', newValue);
    handlePublicizeLocationChange(newValue);
    
    // If publicizing location is disabled, also disable charging for location
    if (!newValue) {
      logger.debug('Disabling chargeForLocation and clearing price');
      handleChargeLocationChange(false);
      handlePriceChange('');
    }
  };

  const handleChargeLocationChangeLocal = (e) => {
    const newValue = e.target.checked;
    logger.debug('handleChargeLocationChangeLocal called with:', newValue);
    handleChargeLocationChange(newValue);
    
    // If charging is disabled, clear related fields
    if (!newValue) {
      logger.debug('Clearing price and freeForRegisteredUsers');
      handlePriceChange('');
      if (data.freeForRegisteredUsers) {
        handleFreeForRegisteredUsersChange(false);
      }
    }
  };

  // Prepare data for the Fees table
  const sampleAmounts = [1, 2, 5, 10, 20, 50, 100];
  const tableData = sampleAmounts.map((amount) => {
    const platformFee = amount * 0.3; // 30% taken from amount
    const stripeFee = amount * 0.014 + 0.2; // 1.4% + 20p
    const totalFees = platformFee + stripeFee;
    const youReceive = amount - totalFees;

    return {
      amount: `£${amount.toFixed(2)}`,
      platformFee: `£${platformFee.toFixed(2)}`,
      stripeFee: `£${stripeFee.toFixed(2)}`,
      totalFees: `£${totalFees.toFixed(2)}`,
      youReceive: `£${youReceive.toFixed(2)}`,
    };
  });

  // Helper to render a quick summary of fees for a chosen price
  function renderFeesSummary(chosenAmount: number) {
    if (chosenAmount <= 0) return null;

    const platformFee = chosenAmount * 0.3; // 30% taken from amount
    const stripeFee = chosenAmount * 0.014 + 0.2; // 1.4% + 20p
    const totalFees = platformFee + stripeFee;
    const youReceive = chosenAmount - totalFees;

    return (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          For each location reveal at £{chosenAmount.toFixed(2)}:
        </Typography>
        <Typography variant="body2">
          Platform fee (30%): £{platformFee.toFixed(2)}
        </Typography>
        <Typography variant="body2">
          Stripe fee (1.4% + 20p): £{stripeFee.toFixed(2)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          You receive per reveal: £{youReceive.toFixed(2)}
        </Typography>
      </>
    );
  }

  const getLocationAccessType = () => {
    if (data.chargeForLocation) return 'charge';
    if (data.freeForRegisteredUsers) return 'free-registered';
    return 'free-everyone';
  };

  const handleLocationAccessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    logger.debug('handleLocationAccessChange called with:', value);

    if (value === 'charge') {
      handleChargeLocationChange(true);
      handlePriceChange(''); // Clear price if charging
      handleFreeForRegisteredUsersChange(false); // Disable free for registered users
      handleFreeForEveryoneChange(false); // Disable free for everyone
    } else if (value === 'free-registered') {
      handleChargeLocationChange(false);
      handlePriceChange(''); // Clear price if free for registered
      handleFreeForRegisteredUsersChange(true);
      handleFreeForEveryoneChange(false); // Disable free for everyone
    } else { // 'free-everyone'
      handleChargeLocationChange(false);
      handlePriceChange(''); // Clear price if free for everyone
      handleFreeForRegisteredUsersChange(false);
      handleFreeForEveryoneChange(true);
    }
  };

  return (
    <Card
      sx={{
        mb: 4,
        position: 'relative',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      {/* Image Preview */}
      <CardMedia
        component="img"
        sx={{ width: isMobile ? '100%' : '65%', height: 'auto' }}
        image={URL.createObjectURL(data.file)}
        alt={data.file.name}
      />

      <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            {/* Description Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label="Catch Details"
                value={data.tags}
                onChange={(e) => handleTagsChange(e.target.value)}
                helperText="Enter some details about how the fish was caught, e.g. bait used, type of rod..."
                multiline
                rows={4}
              />
            </Grid>

            {/* Species Field */}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={[...commonSeaFishSpecies, ...commonCoarseFishSpecies]}
                value={data.species || ''}
                onChange={(_, newValue) => handleSpeciesChange(_, newValue)}
                renderInput={(params) => <TextField {...params} label="Species" size="small" fullWidth />}
              />
            </Grid>

            {/* Weight Field */}
            <Grid item xs={12}>
              <WeightInput 
                value={data.weight} 
                onChange={(value) => {
                  logger.debug('ImageCard - WeightInput onChange called with:', value);
                  logger.debug('pooooo:', data.weight);
                  handleWeightChange(value);
                }}
              />
            </Grid>

            {/* Date Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Date"
                value={data.dateTaken ? new Date(data.dateTaken).toLocaleString() : new Date().toLocaleString()}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            {/* Location Settings Box */}
            <Grid item xs={12}>
              <Box
                sx={{
                  padding: 2,
                  bgcolor: !data.hasExif
                    ? 'action.disabledBackground'
                    : data.publicizeLocation
                      ? 'background.paper'
                      : 'action.disabledBackground',
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Header with info icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 1,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Location Settings
                    </Typography>
                    <IconButton size="small" onClick={handleInfoClick}>
                      <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Controls */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  
                  {/* Publicize Location Checkbox or "unavailable" message */}
                  {data.hasExif ? (
                    <Grid container spacing={2}>
                      {/* First Row: Publicise Location */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={data.publicizeLocation || false}
                              onChange={handlePublicizeChange}
                              disabled={!data.hasExif}
                            />
                          }
                          label={`Publicise location (${data.publicizeLocation})`}
                          sx={{ mb: 1 }}
                        />
                      </Grid>

                      {/* Second Row: Coordinates */}
                      <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Latitude"
                            value={data.latitude || ''}
                            InputProps={{ readOnly: true }}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Longitude"
                            value={data.longitude || ''}
                            InputProps={{ readOnly: true }}
                            disabled
                          />
                        </Grid>
                      </Grid>

                      {/* Third Row: Area with Map Button */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Area"
                            value={data.area || ''}
                            InputProps={{ readOnly: true }}
                            disabled
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenMap(data.latitude, data.longitude)}
                            disabled={!data.latitude || !data.longitude}
                            startIcon={<MapIcon />}
                            sx={{ 
                              minWidth: 'auto', 
                              whiteSpace: 'nowrap',
                              height: '40px'  // Match TextField height
                            }}
                          >
                            View on Map
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.warning" sx={{ py: 1, fontWeight: 'bold' }}>
                        No location data available - photo can still be uploaded
                      </Typography>
                      <Button 
                        size="small" 
                        startIcon={<InfoIcon />}
                        onClick={() => setShowLocationInfo(true)}
                      >
                        More info about location data
                      </Button>
                    </Box>
                  )}

                  {/* Charge Section */}
                  {data.hasExif && data.publicizeLocation && (
                    <Box sx={{ mt: 1 }}>
                      {/* Location Access Options - Always show the radio buttons */}
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                        Location Access:
                      </Typography>
                      
                      <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <RadioGroup
                          value={getLocationAccessType()}
                          onChange={handleLocationAccessChange}
                        >
                          <FormControlLabel
                            value="charge"
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>Charge for location</Typography>
                                {data.chargeForLocation && (
                                  <Chip 
                                    label="Selected" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                          />
                          
                          <FormControlLabel
                            value="free-registered"
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>Free for registered users</Typography>
                                {data.freeForRegisteredUsers && !data.chargeForLocation && (
                                  <Chip 
                                    label="Selected" 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                          />
                          
                          <FormControlLabel
                            value="free-everyone"
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>Free for everyone</Typography>
                                {!data.chargeForLocation && !data.freeForRegisteredUsers && (
                                  <Chip 
                                    label="Selected" 
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </FormControl>

                      {/* Minimum photo uploads input - only show when free for registered users is selected */}
                      {data.freeForRegisteredUsers && !data.chargeForLocation && (
                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Minimum photo uploads required"
                            value={data.minimumPhotoUploads || ''}
                            onChange={(e) => handleMinimumPhotoUploadsChange(e.target.value === '' ? undefined : parseInt(e.target.value))}
                            inputProps={{ min: 0, max: 100 }}
                            helperText="Number of photos users must have uploaded to access this location for free"
                          />
                        </FormControl>
                      )}

                      {/* Show payment setup message only when charging is selected but Stripe isn't set up */}
                      {data.chargeForLocation && !stripeOnboardingComplete && (
                        <Box
                          sx={{
                            bgcolor: 'info.light',
                            p: 2,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 2,
                          }}
                        >
                          <InfoIcon color="info" fontSize="small" />
                          <Typography variant="body2">
                            To charge for location access, you need to complete the payment setup process.
                            Visit your{' '}
                            <Link href="/stripe/setup" color="inherit" underline="always">
                              account setup
                            </Link>{' '}
                            to set this up.
                          </Typography>
                        </Box>
                      )}

                      {/* Show charging options only when Stripe is set up and charging is selected */}
                      {data.chargeForLocation && stripeOnboardingComplete && (
                        <>
                          {/* Price Field, shown only if "Charge for location" is selected */}
                          <FormControl fullWidth size="small" required sx={{ mt: 2 }}>
                            <Autocomplete
                              freeSolo
                              size="small"
                              value={data.price?.toString() || ''}
                              onChange={(_, newValue) => handlePriceChange(newValue)}
                              onInputChange={(_, newValue) => handlePriceChange(newValue)}
                              options={priceOptions.map((price) => price.toString())}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Price"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                  }}
                                />
                              )}
                            />
                            <FormHelperText>
                              Price users will pay to reveal where this fish was caught
                            </FormHelperText>
                          </FormControl>

                          {/* Dynamic fees summary */}
                          {data.price && data.price > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {renderFeesSummary(data.price)}
                              </Typography>
                            </Box>
                          )}

                          {/* Fees Info Button - only show when charging for location */}
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => setOpenFeesInfo(true)}
                            sx={{ mt: 2 }}
                          >
                            Fees Info
                          </Button>
                        </>
                      )}

                      {/* Fees Info Dialog */}
                      <Dialog open={openFeesInfo} onClose={() => setOpenFeesInfo(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Earnings Per Location Reveal</DialogTitle>
                        <DialogContent dividers>
                          <Typography variant="subtitle2" gutterBottom>
                            Each time someone pays to reveal your fishing spot location, the following fees are deducted:
                          </Typography>
                          <Typography gutterBottom>
                            <strong>Platform Fee:</strong> 30% of the price is deducted as a platform fee.
                          </Typography>
                          <Typography gutterBottom>
                            <strong>Stripe Fee:</strong> 1.4% + 20p is deducted for payment processing.
                          </Typography>

                          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                            Below are examples showing how much you'll earn each time someone reveals your location:
                          </Typography>

                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell 
                                  sx={{ 
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 'bold'
                                  }}
                                >
                                  <strong>Price Per Reveal</strong>
                                </TableCell>
                                <TableCell><strong>Total Fees</strong></TableCell>
                                <TableCell 
                                  sx={{ 
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 'bold'
                                  }}
                                >
                                  <strong>You Earn Per Reveal</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tableData.map((row) => (
                                <React.Fragment key={row.amount}>
                                  <TableRow 
                                    hover
                                    onClick={() => setExpandedRow(expandedRow === row.amount ? null : row.amount)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell 
                                      sx={{ 
                                        backgroundColor: theme.palette.primary.light,
                                        color: theme.palette.primary.contrastText,
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {row.amount}
                                    </TableCell>
                                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {row.totalFees}
                                      {expandedRow === row.amount ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </TableCell>
                                    <TableCell 
                                      sx={{ 
                                        backgroundColor: theme.palette.primary.light,
                                        color: theme.palette.primary.contrastText,
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {row.youReceive}
                                    </TableCell>
                                  </TableRow>
                                  {expandedRow === row.amount && (
                                    <TableRow>
                                      <TableCell colSpan={3} sx={{ backgroundColor: theme.palette.grey[50], pl: 4 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                          Platform Fee (30%): {row.platformFee}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Stripe Fee (1.4% + 20p): {row.stripeFee}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>

                          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            Click any row to see fee breakdown. Remember: You can earn multiple times from the same location as different users pay to reveal it.
                          </Typography>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setOpenFeesInfo(false)} autoFocus>
                            Close
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </Box>
                  )}
                </Box>

                {/* Info Popover (existing) */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleInfoClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Paper sx={{ p: 2, maxWidth: 300 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      How Location Settings Work
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. Tick "Publicise location" to make your catch location visible to others.
                    </Typography>
                    <Typography variant="body2" paragraph>
                      2. Optionally, tick "Charge to reveal location" if you want to charge for access
                      {!stripeOnboardingComplete && ' (requires payment setup)'}
                    </Typography>
                    <Typography variant="body2">
                      3. If charging, select a price that others must pay to see your location.
                    </Typography>
                  </Paper>
                </Popover>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Box>

      {/* Loading overlay if still processing */}
      {isSubmitted && !data.processingComplete && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}

      {/* Success icon if complete */}
      {isSubmitted && data.processingComplete && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'green',
          }}
        >
          <CheckCircleIcon fontSize="large" />
        </Box>
      )}

      {/* Location Info Modal */}
      <Modal
        open={showLocationInfo}
        onClose={() => setShowLocationInfo(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          p: 3,
          borderRadius: 2,
          boxShadow: 24
        }}>
          <Typography variant="h6" gutterBottom>
            About Location Data
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your photo can still be uploaded and shared, but without location information. For a photo to contain geo-location data (EXIF), your camera must have location enabled at the time the photo is taken.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Photos downloaded from social media or shared via messaging apps usually have their location data removed. Only original, unedited photos taken with location enabled can publicise a location.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            To enable location tagging:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li>
              <strong>Android:</strong> Open your Camera app settings (often within the Camera app or device's
              main settings). Look for "Location tags," "Geo tagging," or "GPS info" and enable it.
            </li>
            <li>
              <strong>iOS (Apple):</strong> Go to <em>Settings &gt; Privacy &amp; Security &gt; Location Services</em>.
              Ensure <em>Camera</em> is set to "While Using the App" (or "Always") so the camera can embed location data.
            </li>
          </Box>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button onClick={() => setShowLocationInfo(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Card>
  );
};

export default ImageCard;
